// Copyright 2018 Sorama B.V.

import { autoinject, BindingEngine } from "aurelia-framework";
import { Range } from "datatypes/SoramaLocalApi";
import { WeightedSpectrumWithMeasurement } from "datatypes/WeightedSpectrumWithMeasurement";
import { RangeLogic } from "libraries/Math/RangeLogic";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { ComputationModes } from "modules/workflows/Analysis/Spectrum/SpectrumModuleModes";
import { SelectableRange } from "services/FrequencySelection/FrequencySelectionService";
import { Frequency } from "units/Frequency";

export interface WeightedSpectrumWithMeasurementAndEnergy extends WeightedSpectrumWithMeasurement {
    energyInBand: string;
    energyExcludingBand: string;
}

@autoinject
export class FrequencyBandInformationModal implements IModal<FrequencyBandInformationModalState> {
    private state: FrequencyBandInformationModalState;
    private modalInstance: IWorkflowModuleModalInstance<FrequencyBandInformationModalState>;

    constructor(private readonly bindingEngine: BindingEngine) {}

    public activateModal(
        state: FrequencyBandInformationModalState,
        instance: IWorkflowModuleModalInstance<FrequencyBandInformationModalState>
    ) {
        this.modalInstance = instance;

        this.updateState(state);
        this.updateCalculation();
    }

    public updateState?(newState: FrequencyBandInformationModalState): void {
        this.state = newState;
        this.updateCalculation();
    }

    public deactivate(): FrequencyBandInformationModalState {
        this.state.frequencyRanges?.forEach((x) => {
            x.isSelected = false;
        });
        return this.state;
    }

    private toggleSelection(range: SelectableRange): void {
        range.isSelected = !range.isSelected;
        this.updateCalculation();
    }

    protected delete(range: SelectableRange): void {
        this.state.frequencyRanges = this.state.frequencyRanges?.filter((x) => x != range);
        this.updateCalculation();
    }

    protected update(range: SelectableRange, propertyName: "from" | "to", newValue: number): void {
        // Make sure the range is in the state
        const rangeIndex = this.state.frequencyRanges?.findIndex((x) => x == range);

        // Check if range exists
        if (rangeIndex === undefined || rangeIndex < 0) return;

        range[propertyName] = newValue;

        const maxFreq = Math.max(
            ...this.state.spectra.map((x, i) => {
                // Spectra are always sorted.
                return x.getXUnitValue(x.data.length - 1).toUnit(Frequency.hertz);
            })
        );

        const newRange = {
            from: Math.max(range.from, 0),
            to: Math.max(range.from, Math.min(range.to, maxFreq)), // Check out of range and that to >= from
            isSelected: range.isSelected,
        };

        this.state.frequencyRanges.splice(rangeIndex, 1, newRange);

        // Merge overlapping ranges
        this.state.frequencyRanges = RangeLogic.union(this.state.frequencyRanges, (a, b, merged) => {
            merged.isSelected = a.isSelected || b.isSelected;
        });
    }

    private updateCalculation(): void {
        if (!this.state?.spectra) return;

        for (const spectrum of this.state.spectra) {
            const a = spectrum as WeightedSpectrumWithMeasurementAndEnergy;
            a.energyInBand = this.energyInBand(spectrum);
            a.energyExcludingBand = this.energyExcludingBand(spectrum);
        }
    }

    /**
     * Calculates the energy in the selected frequency ranges, note that there is no bounds limitation,
     * any range from 0 Hz to the nyquist frequency can be taken into account.
     */
    private energyInBand(spectrum: WeightedSpectrumWithMeasurement): string {
        if (!this.state.frequencyRanges || this.state.frequencyRanges.filter((x) => x.isSelected).length == 0)
            return "-";

        const value = spectrum.toPowerValueOfRanges(
            spectrum.yUnit,
            this.state.frequencyRanges.filter((x) => x.isSelected)
        );
        return value.value.toFixed(2) + " " + value.unit.unit;
    }

    private energyExcludingBand(spectrum: WeightedSpectrumWithMeasurement): string {
        if (!this.state.frequencyRanges || this.state.frequencyRanges.filter((x) => x.isSelected).length == 0) {
            return "-";
        }

        // Only use frequencies above dc component frequency (20hz for normal measurement, 29 for iV64)
        const validRange = ComputationModes.getValidFrequencyRange(spectrum.usedComputationMode);

        let lowerLimitIndex = spectrum.data.findIndex(
            (x, i) => spectrum.getXUnitValue(i).toUnit(Frequency.hertz) > validRange.from
        );
        lowerLimitIndex = Math.max(0, lowerLimitIndex);

        let upperLimitIndex = spectrum.data.findIndex(
            (x, i) => spectrum.getXUnitValue(i).toUnit(Frequency.hertz) > validRange.to
        );
        if (upperLimitIndex < 0 || upperLimitIndex > spectrum.data.length) {
            upperLimitIndex = spectrum.data.length - 1;
        }

        const bound = <Range>{
            from: spectrum.getXUnitValue(lowerLimitIndex).toUnit(Frequency.hertz),
            to: spectrum.getXUnitValue(upperLimitIndex).toUnit(Frequency.hertz),
        };
        const selectedRanges = this.state.frequencyRanges.filter((x) => x.isSelected);

        const excludingRanges = RangeLogic.subtract(bound, selectedRanges);

        const value = spectrum.toPowerValueOfRanges(spectrum.yUnit, excludingRanges);
        return value.value.toFixed(2) + " " + value.unit.unit;
    }
}

// View-Model for modal needs to be first class in the file
// Please do not try to move this class declaration to the top of the file
// It is loaded in a dynamic way and the order of the classes apparently matters
export class FrequencyBandInformationModalState {
    public spectra: WeightedSpectrumWithMeasurement[] = [];
    public frequencyRanges: SelectableRange[] = [];
}
